// /components/automate/AutomationWrapper.tsx

import React from "react";
import { Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { initiateTwitterLogin } from "@/api/twitter";
import { useToast } from "@/hooks/use-toast";

// Twitter Connection Banner component
function TwitterConnectionBanner({ personaId }: { personaId: string }) {
  const { toast } = useToast();
  const handleConnectTwitter = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    try {
      e.preventDefault();
      const redirectUrl = encodeURIComponent(window.location.href);
      window.location.href = await initiateTwitterLogin(personaId, redirectUrl);
    } catch (error) {
      console.error("Error initiating Twitter login:", error);
      toast({
        description: "Error connecting to Twitter. Please try again later.",
      });
    }
  };

  return (
    <Alert className="mb-6">
      <Twitter className="h-4 w-4" />
      <AlertTitle>Twitter Connection Required</AlertTitle>
      <AlertDescription className="flex items-center justify-between">
        <span>
          Connect your Twitter account to enable autonomous agent interactions
        </span>
        <Button variant="outline" size="sm" onClick={handleConnectTwitter}>
          Connect Twitter
        </Button>
      </AlertDescription>
    </Alert>
  );
}

interface Props {
  selectedPersonaId: string;
  hasTwitterAuth: boolean;
  generalConfig?: any; // or the actual config type
  children: React.ReactNode;
}

/**
 * Renders a banner to connect Twitter if `hasTwitterAuth` is false,
 * and disables (grays out) any interactive elements by wrapping them with
 * `pointer-events-none opacity-60`.
 */
const AutomationWrapper: React.FC<Props> = ({
  selectedPersonaId,
  hasTwitterAuth,
  children,
}) => {
  // If Twitter isn't connected, or user hasn't created generalConfig, we disable everything
  const isDisabled = !hasTwitterAuth; // could also combine: !hasTwitterAuth || !generalConfig
  const wrapperClass = isDisabled ? "pointer-events-none opacity-60" : "";

  return (
    <div className="space-y-6">
      {/* Show Twitter connection banner if not connected */}
      {!hasTwitterAuth && (
        <TwitterConnectionBanner personaId={selectedPersonaId} />
      )}

      {/* Render child content with conditional "disabled" styling */}
      <div className={wrapperClass}>{children}</div>
    </div>
  );
};

export default AutomationWrapper;
